import { ChevronDown, ChevronUp, Plus, Trash2, Zap, X } from 'lucide-react'
import { useROIStore } from '../store'
import { COUNTRY_BENCHMARKS, INDUSTRIES, PROVIDERS, PRODUCT_TYPES } from '../data'
import { clsx } from 'clsx'
import type { Region } from '../types'

interface RegionCardProps {
  region: Region
  isActive: boolean
  canRemove: boolean
  onAddProduct: () => void
}

export function RegionCard({ region, isActive, canRemove, onAddProduct }: RegionCardProps) {
  const {
    setActiveRegion,
    removeRegion,
    updateRegionField,
    removeStackItem,
    applyBestStack,
    getRegionCalculation,
    isCustomerView,
  } = useROIStore()

  const calculation = getRegionCalculation(region.id)
  const improvement = calculation?.improvement ?? 0
  const newRate = calculation?.newRate ?? region.passRate

  // Format volume for display
  const formatVolume = (vol: number) => {
    if (vol >= 1000) return `${(vol / 1000).toFixed(0)}K/mo`
    return `${vol}/mo`
  }

  return (
    <div className={clsx(
      'bg-[var(--bg-secondary)] border rounded-lg overflow-hidden',
      isActive ? 'border-primary/50' : 'border-[var(--border-color)]'
    )}>
      {/* Card Header - Click to expand/collapse */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-[var(--bg-tertiary)]/50 transition-colors"
        onClick={() => setActiveRegion(region.id)}
      >
        <div className="flex items-center gap-3">
          {/* Region name/country as title */}
          <span className="font-medium text-[var(--text-primary)]">
            {region.name || region.country || 'New Region'}
          </span>

          {/* Pass rate summary */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-[var(--text-secondary)]">{region.passRate.toFixed(1)}%</span>
            <span className="text-gray-600">→</span>
            <span className={clsx('font-medium', improvement > 0 ? 'text-success' : 'text-[var(--text-primary)]')}>
              {newRate.toFixed(1)}%
            </span>
          </div>

          {/* Improvement badge */}
          {improvement > 0 && (
            <span className="bg-primary/20 text-primary px-2 py-0.5 rounded text-xs font-medium">
              +{improvement.toFixed(1)}%
            </span>
          )}

          {/* Monthly volume */}
          <span className="text-xs text-[var(--text-muted)]">{formatVolume(region.volume)}</span>
        </div>

        <div className="flex items-center gap-2">
          {/* Remove button */}
          {canRemove && !isCustomerView && (
            <button
              className="p-1.5 text-[var(--text-muted)] hover:text-danger hover:bg-danger/10 rounded transition-colors"
              onClick={(e) => {
                e.stopPropagation()
                if (confirm('Remove this region?')) {
                  removeRegion(region.id)
                }
              }}
            >
              <Trash2 size={14} />
            </button>
          )}

          {/* Expand/Collapse icon */}
          {isActive ? <ChevronUp size={18} className="text-[var(--text-muted)]" /> : <ChevronDown size={18} className="text-[var(--text-muted)]" />}
        </div>
      </div>

      {/* Card Body - Expanded */}
      {isActive && (
        <div className="border-t border-[var(--border-color)] p-4 space-y-6">
          {/* Region Label */}
          {!isCustomerView && (
            <div className="space-y-1.5">
              <label className="text-sm text-[var(--text-secondary)]">Region Label (optional)</label>
              <input
                type="text"
                className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 py-2.5 text-sm text-[var(--text-primary)] placeholder-gray-600"
                value={region.name}
                onChange={(e) => updateRegionField(region.id, 'name', e.target.value)}
                placeholder={region.country || 'e.g., Scenario A, Premium Tier...'}
              />
            </div>
          )}

          {/* Current Setup Section */}
          <div>
            <h4 className="text-xs font-medium text-primary uppercase tracking-wide mb-4">
              Current Setup
            </h4>
            <div className="space-y-4">
              {/* Target Country */}
              <div className="space-y-1.5">
                <label className="text-sm text-[var(--text-secondary)]">Target Country</label>
                <select
                  className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 py-2.5 text-sm text-[var(--text-primary)] appearance-none cursor-pointer"
                  value={region.country}
                  onChange={(e) => updateRegionField(region.id, 'country', e.target.value)}
                  disabled={isCustomerView}
                >
                  <option value="">Select...</option>
                  {COUNTRY_BENCHMARKS.map((c) => (
                    <option key={c.country} value={c.country}>
                      {c.country}
                    </option>
                  ))}
                </select>
              </div>

              {/* Industry - with ARPU display */}
              <div className="space-y-1.5">
                <label className="text-sm text-[var(--text-secondary)]">Industry</label>
                <select
                  className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 py-2.5 text-sm text-[var(--text-primary)] appearance-none cursor-pointer"
                  value={region.industry}
                  onChange={(e) => updateRegionField(region.id, 'industry', e.target.value)}
                  disabled={isCustomerView}
                >
                  <option value="">Select...</option>
                  {INDUSTRIES.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name} {i.arpu.monthly > 0 ? `(~$${i.arpu.monthly.toFixed(0)}/mo ARPU)` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Current Provider */}
              <div className="space-y-1.5">
                <label className="text-sm text-[var(--text-secondary)]">Current Provider</label>
                <select
                  className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 py-2.5 text-sm text-[var(--text-primary)] appearance-none cursor-pointer"
                  value={region.provider}
                  onChange={(e) => updateRegionField(region.id, 'provider', e.target.value)}
                  disabled={isCustomerView}
                >
                  <option value="">Select...</option>
                  {PROVIDERS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Current Product Type */}
              <div className="space-y-1.5">
                <label className="text-sm text-[var(--text-secondary)]">Current Product Type</label>
                <select
                  className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 py-2.5 text-sm text-[var(--text-primary)] appearance-none cursor-pointer"
                  value={region.product}
                  onChange={(e) => updateRegionField(region.id, 'product', e.target.value)}
                  disabled={isCustomerView}
                >
                  <option value="">Select...</option>
                  {PRODUCT_TYPES.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Current Pass Rate with % suffix */}
              <div className="space-y-1.5">
                <label className="text-sm text-[var(--text-secondary)]">Current Pass Rate (%)</label>
                <div className="relative">
                  <input
                    type="number"
                    className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 py-2.5 text-sm text-[var(--text-primary)] pr-8"
                    value={region.passRate || ''}
                    onChange={(e) =>
                      updateRegionField(region.id, 'passRate', Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))
                    }
                    min="0"
                    max="100"
                    step="0.1"
                    disabled={isCustomerView}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] text-sm">%</span>
                </div>
              </div>

              {/* Monthly Volume and Value per Customer - Side by side */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-sm text-[var(--text-secondary)]">Monthly Volume</label>
                  <input
                    type="number"
                    className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 py-2.5 text-sm text-[var(--text-primary)]"
                    value={region.volume || ''}
                    onChange={(e) => updateRegionField(region.id, 'volume', parseInt(e.target.value) || 0)}
                    placeholder="0"
                    min="0"
                    disabled={isCustomerView}
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm text-[var(--text-secondary)]">Value per Customer</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] text-sm">$</span>
                    <input
                      type="number"
                      className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg pl-7 pr-3 py-2.5 text-sm text-[var(--text-primary)]"
                      value={region.valuePerCustomer || ''}
                      onChange={(e) => updateRegionField(region.id, 'valuePerCustomer', parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      min="0"
                      step="0.01"
                      disabled={isCustomerView}
                    />
                  </div>
                </div>
              </div>

              {/* Current Cost per Check */}
              <div className="space-y-1.5">
                <label className="text-sm text-[var(--text-secondary)]">Current Cost per Check</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] text-sm">$</span>
                  <input
                    type="number"
                    className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg pl-7 pr-3 py-2.5 text-sm text-[var(--text-primary)]"
                    value={region.costPerCheck || ''}
                    onChange={(e) => updateRegionField(region.id, 'costPerCheck', parseFloat(e.target.value) || 0)}
                    placeholder="e.g., 0.50"
                    min="0"
                    step="0.01"
                    disabled={isCustomerView}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-[var(--border-color)]" />

          {/* FrankieOne Stack Section */}
          <div>
            <h4 className="text-xs font-medium text-primary uppercase tracking-wide mb-4">
              Verification Pipeline
            </h4>

            {/* Pipeline Items */}
            {region.stack.length === 0 ? (
              <div className="text-center py-8 text-[var(--text-muted)] text-sm bg-[var(--bg-tertiary)]/30 rounded-lg border border-dashed border-[var(--border-color)]">
                No products added. Add products to see pass rate improvements.
              </div>
            ) : (
              <div className="space-y-3">
                {region.stack.map((item, index) => (
                  <div
                    key={item.profile}
                    className="flex items-center gap-3 bg-[#1e2a3a] border border-[#2a3a4a] rounded-lg p-3"
                  >
                    {/* Number circle */}
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary font-medium text-sm">{index + 1}</span>
                    </div>

                    {/* Product info */}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[var(--text-primary)] text-sm">{item.description}</div>
                      <div className="text-xs text-[var(--text-muted)]">
                        +{item.incrementalLift}% lift | {item.passRate}% benchmark
                      </div>
                    </div>

                    {/* Remove button */}
                    {!isCustomerView && (
                      <button
                        className="p-1.5 text-[var(--text-muted)] hover:text-danger transition-colors"
                        onClick={() => removeStackItem(region.id, index)}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Action Buttons - Side by side */}
            {!isCustomerView && (
              <div className="flex gap-3 mt-4">
                <button
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-primary/20 text-primary rounded-lg text-sm font-medium hover:bg-primary/30 transition-colors"
                  onClick={() => applyBestStack(region.id)}
                >
                  <Zap size={14} />
                  Recommended Pipeline
                </button>
                <button
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-[var(--border-color)] rounded-lg text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-gray-500 transition-colors"
                  onClick={onAddProduct}
                >
                  <Plus size={14} />
                  Add Product
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
